import { NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@ngneat/transloco';
import { AuthService } from 'app/core/auth/auth.service';
import { UserDataService } from 'app/core/datas/user-data';
import { Project } from 'app/core/models/base.model';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'compact-project-layout',
    templateUrl: './compact-project.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [FuseLoadingBarComponent, MatButtonModule, MatIconModule, UserComponent, LanguagesComponent, NgIf, RouterOutlet, FuseVerticalNavigationComponent, TranslocoModule]
})
export class CompactProjectLayoutComponent implements OnInit, OnDestroy {
    private _userDataSvc = inject(UserDataService);
    private _authSvc = inject(AuthService);
    isScreenSmall: boolean;
    navigation: Navigation;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    appVersion: string = environment.appVersion;
    debug: boolean = !environment.production;
    @Input() projectid: number;
    @Input() project: Project;
    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
    ) {

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                let newNavigation: FuseNavigationItem[] = [];

                navigation.compactProject.forEach((element: FuseNavigationItem) => {
                    if (element.id === 'detail') {
                        if (this._authSvc.hasBasicRole('app_owner') || this._authSvc.hasBasicRole('project_owner')) {
                            newNavigation.push(element);
                        }
                    } else if (element.id === 'company') {
                        if (this._authSvc.hasBasicRole('app_owner') || this._authSvc.hasBasicRole('project_owner')) {
                            newNavigation.push(element);
                        }
                    } else if (element.id === 'code') {
                        if (this._authSvc.hasBasicRole('app_owner') || this._authSvc.hasBasicRole('project_owner') || this._authSvc.hasBasicRole('project_operator')) {
                            newNavigation.push(element);
                        }
                    } else if (element.id === 'masterdata') {
                        if (this._authSvc.hasBasicRole('app_owner') || this._authSvc.hasBasicRole('project_owner') || this._authSvc.hasBasicRole('project_operator')) {
                            newNavigation.push(element);
                        }
                    } else if (element.id === 'setting') {
                        if (this._authSvc.hasBasicRole('app_owner') || this._authSvc.hasBasicRole('project_owner') || this._authSvc.hasBasicRole('project_operator')) {
                            newNavigation.push(element);
                        }
                    } else if (element.id === 'trace') {
                        if (this._authSvc.hasBasicRole('app_owner') || this._authSvc.hasBasicRole('project_owner') || this._authSvc.hasBasicRole('project_operator')) {
                            newNavigation.push(element);
                        }
                    } else {
                        newNavigation.push(element);
                    }
                });

                navigation.compactProject = newNavigation;

                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    clickBack() {
        this._router.navigate(['select', 'projects']);
    }

    onLogoClick(): void {
        this._navigationService.hijackLogoNavigation();
    }
}
